import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Box, Stack, Button, Accent, Text } from '@nerdwallet/currency';
import classNames from 'classnames';

import _ from 'lodash/';

import {
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
  boolCheckboxSpec,
} from '../../../lib/editing-configs';
import BackgroundColor from '../../background-color/background-color';
import {
  CARD_TREATMENT_ENUM,
  ALIGNMENT_ENUM,
  CONTENT_ALIGNMENT_ENUM,
} from '../lib/constants';
import parseMarkdownLinks from '../../../lib/parse-md-links';
import getTextColor from '../../../lib/text-color-config';
import { getButtonStyle } from '../../../lib/button-style-helper';

import '../flex-card.less';

const isValid = (v) => !_.isNil(v) && !_.isEmpty(v);

const contentSizeEnum = {
  2: 'default',
  1: 'small',
};

const titleSizeEnum = {
  2: 'default',
  3: 'large',
};

const FlexCardItem = ({
  cardImage,
  cardImageMobile,
  cardImageTablet,
  bgOptions,
  eyebrow,
  cardAlignment,
  title,
  subtitle,
  titleSize,
  subhead,
  subheadSize,
  label,
  ctaText,
  ctaHref,
  ctaType,
  desktopCarousel,
  shadowColorTreatment,
  borderSizeTreatment,
  borderColorTreatment,
  cardTreatment,
  textColor,
  parseLinks,
  fixImageHeight,
  imageHeight,
  dangerouslyAllowHTMLInBody,
  allowAlternateCardImage,
  size,
  eyebrowAlignment,
  maxWidthTwoThirds,
  innerPadding,
  titleColorOptions,
  subtitleColorOptions,
  eyebrowColorOptions,
  cardContentVerticalAlignment,
}) => {
  const linkParser = (content) =>
    parseLinks ? parseMarkdownLinks(content, false) : content;

  const cardImageSrcMap = {
    lg: cardImage,
    md: cardImageTablet,
    sm: cardImageMobile,
  };

  const getImageSrcUrl = () => {
    if (allowAlternateCardImage && cardImageSrcMap[size]) {
      return cardImageSrcMap[size];
    }
    return cardImage;
  };

  const htmlOutputParser = (subheadContent) => {
    if (dangerouslyAllowHTMLInBody) {
      // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
      return <span dangerouslySetInnerHTML={{ __html: subheadContent }} />;
    }
    return linkParser(subheadContent);
  };

  const borderTreatment =
    cardTreatment === CARD_TREATMENT_ENUM.BORDER
      ? {
          border: `${borderSizeTreatment} solid ${borderColorTreatment}`,
        }
      : '';

  const shadowTreatment =
    cardTreatment === CARD_TREATMENT_ENUM.BOX_SHADOW
      ? {
          boxShadow: `0px 1px 10px -1px ${shadowColorTreatment}`,
        }
      : '';
  const imageHeightStyle = fixImageHeight
    ? { height: imageHeight, overflow: 'hidden' }
    : {};

  const buttonStyle = getButtonStyle(ctaType);

  const { textColor: titleColor } = getTextColor(titleColorOptions);
  const { textColor: subtitleColor } = getTextColor(subtitleColorOptions);
  const { textColor: eyebrowColor } = getTextColor(eyebrowColorOptions);

  const removeInnerPadding =
    innerPadding === 'all' || innerPadding === 'horizontal';

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <Box
          padding="2"
          className={classNames('flex-card-item', {
            carousel: desktopCarousel,
          })}
        >
          <Box
            borderRadius="2"
            height="100"
            style={{
              justifyContent: cardContentVerticalAlignment,
              backgroundColor,
              ...borderTreatment,
              ...shadowTreatment,
            }}
            className="flex-card-container"
          >
            <Box
              className={classNames([
                `flex-card-content-wrapper`,
                {
                  'remove-inner-padding': removeInnerPadding,
                },
              ])}
            >
              {isValid(getImageSrcUrl()) && (
                <Box style={imageHeightStyle}>
                  <img
                    alt={title}
                    src={getImageSrcUrl()}
                    className="flex-card-image"
                  />
                </Box>
              )}
              <Box
                style={textColor}
                className={classNames('flex-card-content', [
                  {
                    maxWidthTwoThirdsSpaces: maxWidthTwoThirds,
                    'remove-inner-padding': removeInnerPadding,
                    'keep-horizontal': innerPadding === 'horizontal',
                    'match-to-subtitle': !!subtitle,
                  },
                ])}
                paddingX="4"
              >
                <Stack spacing="3">
                  {isValid(eyebrow) && (
                    <Box
                      id="flex-card-item-eyebrow"
                      style={{
                        textAlign: eyebrowAlignment,
                        color: eyebrowColor,
                      }}
                    >
                      <Accent size="small">{eyebrow}</Accent>
                    </Box>
                  )}
                  <Box style={{ color: titleColor }}>
                    <Text
                      id="flex-card-item-title"
                      align={cardAlignment}
                      component="h2"
                      size={titleSizeEnum[titleSize]}
                      bold
                    >
                      {title}
                    </Text>
                  </Box>
                  <Box
                    paddingBottom="2"
                    style={{ textAlign: cardAlignment, color: subtitleColor }}
                  >
                    <Text
                      id="flex-card-item-subtitle"
                      component="p"
                      size="x-small"
                      bold
                    >
                      {subtitle}
                    </Text>
                  </Box>
                  <Text
                    id="flex-card-item-subhead"
                    align={cardAlignment}
                    component="p"
                    size={contentSizeEnum[subheadSize]}
                  >
                    {htmlOutputParser(subhead)}
                  </Text>
                </Stack>
              </Box>
            </Box>
            <Box
              paddingX={removeInnerPadding ? '0' : '3'}
              paddingBottom={removeInnerPadding ? '0' : '5'}
            >
              <Stack spacing="3">
                {isValid(label) && (
                  <Box
                    paddingTop="4"
                    style={textColor}
                    className={classNames('custom-inner-padding', [
                      {
                        'keep-horizontal': innerPadding === 'horizontal',
                      },
                    ])}
                  >
                    <Text size="small" align={cardAlignment} component="p">
                      {linkParser(label)}
                    </Text>
                  </Box>
                )}
                {ctaText && ctaHref && (
                  <Box
                    align={cardAlignment}
                    paddingTop="2"
                    className={classNames('custom-inner-padding', [
                      {
                        'keep-horizontal': innerPadding === 'horizontal',
                      },
                    ])}
                  >
                    <Button {...buttonStyle} href={ctaHref}>
                      {ctaText}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </BackgroundColor>
  );
};

FlexCardItem.getEditingConfig = (componentDocument) => {
  const optionalImageHeight = _.get(
    componentDocument,
    'props.fixImageHeight',
    false
  )
    ? [
        stringInputSpec(
          'imageHeight',
          'Image fixed height in px (default is 30px)'
        ),
      ]
    : [];
  const alternateCardImageInputs = _.get(
    componentDocument,
    'props.allowAlternateCardImage',
    false
  )
    ? [
        stringInputSpec('cardImageTablet', 'Tablet Image Url'),
        stringInputSpec('cardImageMobile', 'Mobile Image Url'),
      ]
    : [];
  return [
    stringInputSpec('title', 'Card Title'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Card Title Color',
      'Card Title hex color override'
    ),
    stringInputSpec('subtitle', 'Card Subtitle'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'subtitleColorOptions',
      'Card Subtitle Color',
      'Card Subtitle hex color override'
    ),
    stringSelectSpec('titleSize', 'Card Title Size', [
      { label: 'Default', value: '2' },
      { label: 'Large', value: '3' },
    ]),
    boolCheckboxSpec('allowAlternateCardImage', 'Allow alternate Image URL?'),
    boolCheckboxSpec('maxWidthTwoThirds', 'Max width two thirds?'),
    stringSelectSpec('innerPadding', 'Card Inner Padding', [
      { label: 'None', value: 'none' },
      { label: 'Remove all', value: 'all' },
      { label: 'Keep Horizontal', value: 'horizontal' },
    ]),
    stringInputSpec('cardImage', 'Image Url'),
    ...alternateCardImageInputs,
    boolCheckboxSpec('fixImageHeight', 'Set Card image height?'),
    ...optionalImageHeight,
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringInputSpec('eyebrow', 'Card Eyebrow Text'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'eyebrowColorOptions',
      'Eyebrow Color',
      'Eyebrow hex color override'
    ),
    stringSelectSpec('eyebrowAlignment', 'Eyebrow Alignment', [
      { label: 'Left', value: ALIGNMENT_ENUM.LEFT },
      { label: 'Center', value: ALIGNMENT_ENUM.CENTER },
      { label: 'Right', value: ALIGNMENT_ENUM.RIGHT },
    ]),
    stringTextAreaSpec('subhead', 'Card Text Content', {
      help: `Want a hyperlink? Write it like this: [Hi](www.thing.com). NW links should be relative, e.g., [Home](/home)`,
    }),
    boolCheckboxSpec('dangerouslyAllowHTMLInBody', 'Allow HTML in body text?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
    stringSelectSpec('subheadSize', 'Card Text Content Size', [
      { label: 'Default', value: '2' },
      { label: 'Small', value: '1' },
    ]),
    stringTextAreaSpec('label', 'Card label', {
      help: `Want a hyperlink? Write it like this: [Hi!](https://www.thing.com).
            NW links should be relative, e.g., [Home](/home)`,
    }),
    stringInputSpec('ctaText', 'Action Button Label'),
    stringInputSpec('ctaHref', 'Action Button URL'),
    stringSelectSpec('ctaType', 'Action Button Style', [
      { label: 'Primary', value: 'primary' },
      { label: 'Secondary', value: 'secondary' },
      { label: 'Tertiary', value: 'tertiary' },
    ]),
    stringSelectSpec('cardAlignment', 'Card Text Alignment', [
      { label: 'Left', value: ALIGNMENT_ENUM.LEFT },
      { label: 'Center', value: ALIGNMENT_ENUM.CENTER },
      { label: 'Right', value: ALIGNMENT_ENUM.RIGHT },
    ]),
    stringSelectSpec(
      'cardContentVerticalAlignment',
      'Card Content Vertical Alignment',
      [
        { label: 'Top', value: CONTENT_ALIGNMENT_ENUM.TOP },
        { label: 'Center', value: CONTENT_ALIGNMENT_ENUM.CENTER },
        { label: 'Bottom', value: CONTENT_ALIGNMENT_ENUM.BOTTOM },
        { label: 'Space evenly', value: CONTENT_ALIGNMENT_ENUM.SPACE_EVENLY },
        { label: 'Space around', value: CONTENT_ALIGNMENT_ENUM.SPACE_AROUND },
      ]
    ),
  ];
};

FlexCardItem.displayName = 'FlexCardItem';

FlexCardItem.propTypes = {
  size: PropTypes.string,
  borderSizeTreatment: PropTypes.string,
  borderColorTreatment: PropTypes.string,
  cardImage: PropTypes.string,
  cardTreatment: PropTypes.string,
  shadowColorTreatment: PropTypes.string,
  ...BackgroundColor.propTypes,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  eyebrow: PropTypes.string,
  label: PropTypes.string,
  subhead: PropTypes.string,
  subheadSize: PropTypes.string,
  titleSize: PropTypes.string,
  ctaType: PropTypes.string,
  ctaText: PropTypes.string,
  cardAlignment: PropTypes.string,
  ctaHref: PropTypes.string,
  cardsPerRow: PropTypes.string,
  fixImageHeight: PropTypes.bool,
  desktopCarousel: PropTypes.bool,
  imageHeight: PropTypes.string,
  dangerouslyAllowHTMLInBody: PropTypes.bool,
  allowAlternateCardImage: PropTypes.bool,
  maxWidthTwoThirds: PropTypes.bool,
  innerPadding: PropTypes.string,
  cardContentVerticalAlignment: PropTypes.string,
  children: PropTypes.node,
};

FlexCardItem.defaultProps = {
  size: 'sm',
  cardTreatment: CARD_TREATMENT_ENUM.NONE,
  shadowColorTreatment: '',
  ...BackgroundColor.defaultProps,
  title: 'Title goes here',
  subtitle: '',
  subheadSize: '2',
  titleSize: '2',
  ctaType: 'primary',
  fixImageHeight: false,
  parseLinks: true,
  imageHeight: '30px',
  cardAlignment: ALIGNMENT_ENUM.LEFT,
  eyebrowAlignment: ALIGNMENT_ENUM.LEFT,
  dangerouslyAllowHTMLInBody: false,
  allowAlternateCardImage: false,
  maxWidthTwoThirds: false,
  innerPadding: 'none',
  cardContentVerticalAlignment: CONTENT_ALIGNMENT_ENUM.TOP,
  children: null,
};

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(FlexCardItem);
